<template>
    <div class="statistics app__page">
        <div class="statistics__page-header app__page-header">
            <project-selector
                class="statistics__project-selector"
                :projects="statisticsProjects"
                v-model="currentProjectId"
            ></project-selector>
        </div>
        <div class="statistics__content">
            <div class="statistics__applications-ranking">
                <div class="statistics__applications-ranking-title">
                    {{ $t("statistics.topRankedApplicationsTitle", {count: topRankedApplications.length}) }}
                </div>
                <div class="statistics__applications-ranking-content">
                    <div
                        class="statistics__top-ranked-application"
                        v-for="(application, index) in topRankedApplications"
                        @click="selectRankedApplication(index)"
                        :key="application.id"
                    >
                        <application-thumb :application="application"></application-thumb>
                        <div class="statistics__top-ranked-application-data">
                            <div class="statistics__top-ranked-application-value">{{ application.launchesCount }}</div>
                            <div class="statistics__top-ranked-application-label">
                                {{ $tc("statistics.rankLabel") }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="statistics__chart">
                <div class="statistics__chart-title">
                    {{ $t("statistics.rankedApplicationsTitle", {count: rankedApplications.length}) }}
                </div>
                <div class="statistics__chart-container_most-used">
                    <canvas ref="applicationsTopChart"></canvas>
                </div>
            </div>
            <div class="statistics__chart">
                <div class="statistics__chart-title statistics__chart-title_applications_launches">
                    {{ $tc("statistics.applicationLaunchesTitle") }}
                </div>
                <form-select-group
                    class="statistics__application-selector"
                    :label="$tc('statistics.applicationSelect')"
                    :options="applicationsWithStatisticsOptions"
                    @input="selectApplicationWithStatistics"
                    v-model="selectedApplicationId"
                >
                </form-select-group>
                <div class="statistics__chart-average-value">
                    {{ averageUsageValue }}
                    <div class="statistics__charts-average-label">{{ $tc("statistics.averageUsage") }}</div>
                </div>
                <div class="statistics__chart-container_usage">
                    <canvas ref="applicationsLaunchesChart"></canvas>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ApplicationThumb from "@/components/ApplicationThumb.vue";
import Chart from "chart.js";

import {FormSelectGroup, store} from "atom";
import ProjectSelector from "../components/ProjectSelector";
import moment from "moment";
const {createNamespacedHelpers} = store;
const {mapActions, mapGetters, mapState} = createNamespacedHelpers("arcman");
const USAGE_STATS_LENGTH = 30; // days
/**
 * Renderer for the statistics page of the application
 */
export default {
    name: "statistics",
    components: {
        ApplicationThumb,
        FormSelectGroup,
        ProjectSelector,
    },
    computed: {
        ...mapGetters(["currentLocale"]),
        ...mapState(["applications", "statisticsApplications", "projects"]),
        statisticsProjects() {
            let projectsList = this.projects.slice(0);
            projectsList.unshift(null);
            return projectsList;
        },
        currentProject() {
            return this.projects.find(element => element.id === this.currentProjectId);
        },
        applicationsLaunchesChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                legend: {
                    display: false,
                },
                scales: {
                    xAxes: [
                        {
                            gridLines: {
                                display: false,
                            },
                        },
                    ],
                    yAxes: [
                        {
                            ticks: {
                                callback: value => {
                                    if (value % 1 === 0) {
                                        return value;
                                    }
                                },
                            },
                        },
                    ],
                },
            };
        },
        applicationsTopChartOptions() {
            return {
                legend: {
                    display: false,
                },
                interaction: {
                    mode: "index",
                    axis: "y",
                },
                events: ["click"],
                onClick: (e, i) => {
                    this.selectRankedApplication(i[0]._index);
                },
            };
        },
        applicationsMap() {
            return this.applications.reduce((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, {});
        },
        applicationsWithStatistics() {
            let applicationsWithStatistics = [];
            let index = 0;
            for (let applicationWithStatistics of this.statisticsApplications) {
                let application = this.applicationsMap[applicationWithStatistics.id];
                if (application) {
                    application.launchesCount = applicationWithStatistics.launchesCount;
                    application.launchesData = applicationWithStatistics.launchesData;
                    application.datasetColor = this.colorsPalette(index);
                    applicationsWithStatistics.push(application);
                    index += 1;
                }
            }
            return applicationsWithStatistics;
        },
        applicationsWithStatisticsOptions() {
            return this.applicationsWithStatistics
                .map(item => item)
                .sort((a, b) => (b.name[this.currentLocale] > a.name[this.currentLocale] ? -1 : 1))
                .map(item => ({text: item.name[this.currentLocale], value: item.id}));
        },
        rankedApplications() {
            return this.applicationsWithStatistics.slice(0, 20);
        },
        topRankedApplications() {
            return this.rankedApplications.slice(0, 5);
        },
        applicationsLaunchesChartTimeSpan() {
            let previousDays = [];
            for (let days = USAGE_STATS_LENGTH; days >= 0; days -= 1) {
                previousDays.push(moment().subtract(days, "days"));
            }
            return previousDays;
        },
        applicationsLaunchesChartDates() {
            return this.applicationsLaunchesChartTimeSpan.map((day, index) =>
                day.date() === 1 || day.date() === 2 || index === 0
                    ? [day.format("DD"), day.format("MMM")]
                    : [day.format("DD"), ""]
            );
        },
        applicationUsages() {
            return this.applicationsLaunchesChartTimeSpan.map(
                day =>
                    this.selectedApplication.launchesData.find(item => item.date === day.format("YYYY-MM-DD"))
                        ?.launches || 0
            );
        },
        applicationsLaunchesChartData() {
            return this.selectedApplication?.launchesData?.length
                ? {
                      labels: this.applicationsLaunchesChartDates.map((date, index) => {
                          return index % 2 === 0 ? date : "";
                      }),
                      datasets: [
                          {
                              data: this.applicationUsages,
                              label: this.selectedApplication.name[this.currentLocale],
                              borderColor: "rgba(119,117,189,1)",
                              backgroundColor: "rgba(119,117,189,0.3)",
                              borderJoinStyle: "round",
                              tension: 0,
                              pointRadius: 3,
                              pointBackgroundColor: "rgba(119,117,189,1)",
                          },
                      ],
                  }
                : {};
        },
        applicationsTopChartData() {
            return {
                labels: this.rankedApplications.map(item => item.name[this.currentLocale]),
                datasets: [
                    {
                        data: this.rankedApplications.map(item => item.launchesCount),
                        backgroundColor: this.rankedApplications.map(item => item.datasetColor),
                    },
                ],
            };
        },
        selectedApplicationName() {
            return this.selectedApplication ? this.selectedApplication.name[this.currentLocale] : "";
        },
        averageUsageValue() {
            if (!this.selectedApplication?.launchesData) {
                return 0;
            }
            const usages = this.applicationUsages.reduce((sum, val) => sum + val);
            return Math.round((10 * usages) / USAGE_STATS_LENGTH) / 10;
        },
    },
    watch: {
        applicationsLaunchesChartData(newValue, oldValue) {
            this.applicationsLaunchesChart.data = newValue;
            this.applicationsLaunchesChart.update();
        },
        applicationsTopChartData(newValue, oldValue) {
            this.applicationsTopChart.data = newValue;
            this.applicationsTopChart.update();
        },
        rankedApplications: {
            handler(newValue, oldValue) {
                if (newValue.length) {
                    this.selectedApplication = newValue[0];
                }
            },
            immediate: true,
        },
        selectedApplication(newValue, oldValue) {
            this.selectedApplicationId = newValue?.id;
        },
        currentProjectId: {
            handler(newValue, oldValue) {
                this.loadStatistics(newValue);
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions({
            loadStatistics: "LOAD_STATISTICS",
        }),
        selectApplicationWithStatistics(event) {
            this.selectedApplication = this.applicationsWithStatistics.find(item => item.id === event);
        },
        selectRankedApplication(index) {
            this.selectedApplication = this.rankedApplications[index];
        },
        colorsPalette(index) {
            // return hsl with brightness varying from 36 to 90
            const brightness = -154.278 / (index + 2.857) + 90;
            return `hsl(242deg 35% ${brightness}%)`;
        },
    },
    data() {
        return {
            applicationsLaunchesChart: {},
            applicationsTopChart: {},
            selectedApplication: null,
            selectedApplicationId: "",
            currentProjectId: "",
        };
    },
    mounted() {
        this.applicationsTopChart = new Chart(this.$refs.applicationsTopChart, {
            type: "horizontalBar",
            data: this.applicationsTopChartData,
            options: this.applicationsTopChartOptions,
        });
        this.applicationsLaunchesChart = new Chart(this.$refs.applicationsLaunchesChart, {
            type: "line",
            data: this.applicationsLaunchesChartData,
            options: this.applicationsLaunchesChartOptions,
        });
    },
};
</script>

<style lang="scss" scoped>
.statistics::v-deep {
    @apply h-full px-8;

    .statistics__page-header {
        @apply flex flex-row items-center pt-6 pb-4;
    }

    .statistics__content {
        @apply flex flex-col items-center mt-4 pb-24;
    }

    .statistics__project-selector {
        @apply flex flex-row items-center;
        width: calc(100% - #{$max-button-width});
        @screen md {
            width: 20%;
        }

        .sah-form-select-group__label {
            @apply mr-4;
        }

        .sah-form-select-group__select {
            @apply w-full;
            min-height: $statistics-project-selector-height;
        }
    }

    .statistics__applications-ranking {
        @apply flex flex-col items-center;
    }

    .statistics__applications-ranking-title,
    .statistics__chart-title {
        @apply mb-8 mt-20 font-bold;
        font-size: $font-medium;
        line-height: 1;
    }

    .statistics__applications-ranking-title {
        @apply mt-8;
    }

    .statistics__chart-title_applications_launches {
        @apply mb-4;
    }

    .statistics__applications-ranking-content {
        @apply flex justify-center flex-wrap;
    }

    .statistics__top-ranked-application {
        @apply flex items-center justify-center m-4 shadow-lg rounded-lg
            border border-solid py-2 pr-5;
        border-color: $border-top-ranked-app-color;
        &:hover {
            @apply cursor-pointer;
        }
    }

    .statistics__top-ranked-application-data {
        @apply ml-4 flex flex-col items-end;
    }

    .statistics__top-ranked-application-value {
        @apply font-bold;
        font-size: $font-large;
    }

    .statistics__chart {
        @apply flex flex-col items-center w-3/4 mx-auto relative;
        .statistics__chart-container_most-used {
            @apply h-auto w-full;
        }
        .statistics__chart-container_usage {
            @apply w-full;
            height: $statistics-graph-height;
        }
    }

    .statistics__application-selector {
        @apply flex flex-row items-center mb-8;

        .sah-form-select-group__label {
            @apply mr-2;
        }

        .sah-form-select-group__options {
            @apply overflow-y-scroll;
            max-height: 16rem;
        }
    }

    .statistics__chart-average-value {
        @apply w-full;
        font-size: $font-huge;
        color: $statistics-average-value-color;
        transform: translate($statistics-average-value-offset, $statistics-average-value-offset);
        margin-top: -$statistics-average-value-offset;

        .statistics__charts-average-label {
            @apply inline;
            font-size: $font-medium;
        }
    }
}
</style>
